import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Download, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_BASE = 'https://api.glowytics.com';
const KOTON_BRAND_ID = '062be473-3b2b-4bad-a2e3-a31995e02d57';

const DAY_OPTIONS = [
  { value: 30, label: 'Son 30 gün' },
  { value: 90, label: 'Son 90 gün' },
  { value: 180, label: 'Son 180 gün' },
];

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtPct = (n, d = 1) => `%${(Number(n || 0) * 100).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return `${(v / 1e9).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} milyar`;
  if (v >= 1e6) return `${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} milyon`;
  if (v >= 1e3) return `${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} bin`;
  return fmtInt(v);
};
const trDate = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  return `${Number(d)} ${months[Number(m) - 1]} ${y}`;
};
const trDateShort = (s) => {
  if (!s) return '';
  const [, m, d] = s.split('-');
  const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  return `${Number(d)} ${months[Number(m) - 1]}`;
};

// GA4 olay adlarının yönetim-dostu Türkçe karşılıkları.
const EVENT_LABELS = {
  view_item_list: 'Ürün listesi görüntüleme',
  user_engagement: 'Kullanıcı etkileşimi',
  screen_view: 'Ekran görüntüleme (uygulama)',
  view_search_results: 'Arama sonucu görüntüleme',
  page_view: 'Sayfa görüntüleme',
  view_item: 'Ürün görüntüleme',
  search: 'Arama',
  session_start: 'Oturum başlangıcı',
  view_promotion: 'Promosyon görüntüleme',
  first_visit: 'İlk ziyaret',
  add_to_cart: 'Sepete ekleme',
  begin_checkout: 'Ödemeye başlama',
  purchase: 'Satın alma',
  select_item: 'Ürün seçme',
  select_promotion: 'Promosyon seçme',
  scroll: 'Sayfa kaydırma',
  add_shipping_info: 'Kargo bilgisi girme',
  add_payment_info: 'Ödeme bilgisi girme',
  remove_from_cart: 'Sepetten çıkarma',
  click: 'Tıklama',
};
const evLabel = (name) => EVENT_LABELS[name] || name;

// Son 14 günün günlük olay hacmi — basit dikey bar grafiği (harici kütüphane yok,
// yazdırmaya uygun). Ücretsiz 1M tavanı kesikli çizgiyle gösterilir.
function DailyBars({ daily, freeLimit }) {
  if (!daily || daily.length === 0) return null;
  const maxVal = Math.max(...daily.map((d) => d.events), freeLimit) * 1.08;
  const limitPct = (freeLimit / maxVal) * 100;
  return (
    <div className="mt-6">
      <div className="relative" style={{ height: 200 }}>
        {/* ücretsiz tavan çizgisi */}
        <div className="absolute left-0 right-0 border-t-2 border-dashed border-rose-400 z-10"
          style={{ bottom: `${limitPct}%` }}>
          <span className="absolute -top-5 right-0 text-[10px] font-semibold text-rose-500 bg-white px-1">
            Ücretsiz tavan {fmtCompact(freeLimit)}/gün
          </span>
        </div>
        {/* barlar */}
        <div className="absolute inset-0 flex items-end justify-between gap-1.5">
          {daily.map((d) => {
            const hPct = Math.max((d.events / maxVal) * 100, 1);
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group">
                <div className="text-[9px] font-semibold text-gray-500 mb-0.5 tabular-nums">
                  {(d.events / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M
                </div>
                <div className="w-full rounded-t bg-gray-800 group-hover:bg-gray-900 transition-colors"
                  style={{ height: `${hPct}%` }} title={`${trDate(d.date)}: ${fmtInt(d.events)} olay`} />
              </div>
            );
          })}
        </div>
      </div>
      {/* tarih etiketleri */}
      <div className="flex items-center justify-between gap-1.5 mt-1.5">
        {daily.map((d) => (
          <div key={d.date} className="flex-1 text-center text-[9px] text-gray-400 leading-tight">
            {trDateShort(d.date)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Slide chrome ────────────────────────────────────────────────────────────
function Slide({ n, kicker, children, dark = false }) {
  return (
    <section className={`gt-slide ${dark ? 'bg-[#0b0b0d] text-white' : 'bg-white text-gray-900'} rounded-2xl border ${dark ? 'border-black' : 'border-gray-200'} shadow-sm overflow-hidden`}>
      <div className="px-8 md:px-14 py-10 md:py-14">
        {(n || kicker) && (
          <div className={`flex items-center gap-3 mb-8 ${dark ? 'text-white/50' : 'text-gray-400'}`}>
            {n && <span className="text-xs font-mono tracking-widest">{n}</span>}
            {n && <span className={`h-px w-8 ${dark ? 'bg-white/25' : 'bg-gray-300'}`} />}
            {kicker && <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">{kicker}</span>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

export default function KotonGa4Business() {
  const { selectedBrand } = useAuth();
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const deckRef = useRef(null);

  const isKoton = selectedBrand?.id === KOTON_BRAND_ID;

  // PDF export — her slaytı görüntü olarak yakalayıp tek dosyada birleştirir
  // (window.print değil; sayfadaki içeriğin birebir PDF'i).
  const handleExportPDF = useCallback(async () => {
    if (exporting || !deckRef.current) return;
    setExporting(true);
    try {
      const slides = Array.from(deckRef.current.querySelectorAll('.gt-slide'));
      if (!slides.length) return;
      let pdf = null;
      for (let i = 0; i < slides.length; i++) {
        const el = slides[i];
        const w = el.offsetWidth || 900;
        const h = el.offsetHeight || 600;
        const canvas = await html2canvas(el, {
          scale: 2, useCORS: true, backgroundColor: '#ffffff',
          logging: false, imageTimeout: 20000, windowWidth: w,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const orient = w >= h ? 'landscape' : 'portrait';
        if (!pdf) pdf = new jsPDF({ orientation: orient, unit: 'px', format: [w, h] });
        else pdf.addPage([w, h], orient);
        pdf.addImage(imgData, 'JPEG', 0, 0, w, h);
      }
      const stamp = (data?.dateRange?.to || '').replace(/[^0-9]/g, '') || 'rapor';
      pdf.save(`Koton_GA4_360_Sunum_${stamp}.pdf`);
    } catch (e) {
      console.error('PDF export failed', e);
      alert('PDF oluşturulurken hata oluştu. Konsolu kontrol edin.');
    } finally {
      setExporting(false);
    }
  }, [exporting, data]);

  const fetchData = async () => {
    if (!isKoton) return;
    try {
      setLoading(true);
      setError('');
      const url = new URL(`${API_BASE}/api/koton-ga4-business/overview`);
      url.searchParams.set('days', String(days));
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || j?.detail || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setData(null);
      setError(e?.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, days]);

  const vol = data?.volume;
  const daily = data?.dailyVolume || [];
  const comp = data?.composition;
  const funnel = data?.funnel || [];
  const worst = data?.bottlenecks?.[0];

  // ── Gate ──────────────────────────────────────────────────────────────────
  if (!selectedBrand?.id) return <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>;
  if (!isKoton) {
    return (
      <div className="p-6">
        <div className="rounded-2xl p-10 text-center border border-amber-200 bg-amber-50">
          <h2 className="text-lg font-bold text-gray-900">Bu sunum yalnızca Koton’a özeldir</h2>
          <p className="mt-1 text-sm text-gray-600">Sadece Koton markası seçiliyken görüntülenebilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <style>{`
        @media print {
          .gt-noprint { display: none !important; }
          .gt-deck { gap: 0 !important; }
          .gt-slide { break-inside: avoid; page-break-after: always; box-shadow: none !important; border-radius: 0 !important; border: none !important; }
          body { background: #fff !important; }
        }
      `}</style>

      {/* Kontrol çubuğu — yazdırmada gizli */}
      <div className="gt-noprint sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">Koton · GA4 360 Yönetim Sunumu</div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none"
            >
              {DAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button onClick={fetchData} disabled={loading} title="Yenile"
              className="flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleExportPDF} disabled={exporting || !data}
              className="flex items-center gap-2 px-3 h-9 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-black disabled:opacity-50">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exporting ? 'PDF hazırlanıyor…' : 'PDF indir'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-4 py-6">
        {error && <div className="gt-noprint mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        {loading && !data && <div className="p-16 text-center text-gray-400">Yükleniyor…</div>}

        {data && (
          <div ref={deckRef} className="gt-deck flex flex-col gap-6">

            {/* ── Kapak ──────────────────────────────────────────────────── */}
            <Slide dark>
              <div className="min-h-[300px] flex flex-col justify-between">
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-white/50">Koton · Ölçüm Altyapısı</div>
                <div className="py-10">
                  <h1 className="text-4xl md:text-5xl font-black leading-tight">GA4 360’a Geçiş</h1>
                  <p className="mt-4 text-lg text-white/70 max-w-xl">
                    Yönetim değerlendirmesi — bugün nerede sınırlandığımız, geçişin kısa vadede ne kazandıracağı ve önerimiz.
                  </p>
                </div>
                <div className="flex flex-wrap gap-x-10 gap-y-2 text-sm text-white/50 border-t border-white/10 pt-5">
                  <span>Veri kaynağı: Koton GA4 (canlı)</span>
                  <span>Dönem: {trDate(data.dateRange?.from)} – {trDate(data.dateRange?.to)}</span>
                  <span>Analiz penceresi: son {data.windowDays} gün</span>
                </div>
              </div>
            </Slide>

            {/* ── 01 Yönetici Özeti ──────────────────────────────────────── */}
            {vol && (
              <Slide n="01" kicker="Yönetici Özeti">
                <p className="text-2xl md:text-[26px] font-semibold leading-snug text-gray-900 max-w-2xl">
                  Trafiğimiz, ücretsiz GA4’ün tasarlandığı hacmin çok üzerine çıktı. Bunun sonucunda
                  verimizin büyük bölümü ham olarak elimize ulaşmıyor ve raporlarımız tahmine dayanıyor.
                </p>
                <p className="mt-5 text-[15px] leading-relaxed text-gray-600 max-w-2xl">
                  GA4 360, bu iki tavanı (ham veri aktarımı ve örnekleme) kaldırır. Böylece kararlarımızı
                  tam ve doğru veriyle alır, aşağıda tespit ettiğimiz dönüşüm kaçaklarını segment bazında
                  hedefleyerek kısa vadede kazanca çeviririz.
                </p>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-gray-200 rounded-xl overflow-hidden border border-gray-200">
                  {[
                    { k: 'Günlük olay hacmimiz', v: fmtCompact(vol.avgEventsPerDay), s: `son ${vol.basis?.daysCounted || 30} gün ort. · zirve ${fmtCompact(vol.peakEventsPerDay)}` },
                    { k: 'Ham veride kayıp', v: fmtPct(vol.freeBqDroppedPct, 0), s: 'ücretsiz aktarım tavanı yüzünden' },
                    { k: 'Öncelikli kaçak', v: worst ? fmtPct(worst.abandonRate, 0) : '—', s: worst ? `${worst.fromLabel} → ${worst.toLabel}` : '' },
                  ].map((m) => (
                    <div key={m.k} className="bg-white p-5">
                      <div className="text-xs font-medium text-gray-500">{m.k}</div>
                      <div className="mt-1 text-3xl font-black text-gray-900">{m.v}</div>
                      <div className="mt-0.5 text-xs text-gray-400">{m.s}</div>
                    </div>
                  ))}
                </div>
              </Slide>
            )}

            {/* ── 02 Günlük hacmimiz: kaynağı + 14 günlük trend ──────────── */}
            {vol && (
              <Slide n="02" kicker="Günlük Hacmimiz">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Günde ortalama {fmtCompact(vol.avgEventsPerDay)} olay</h2>
                <p className="mt-3 text-[15px] leading-relaxed text-gray-600 max-w-2xl">
                  Bu rakam bir tahmin değil, <span className="font-semibold text-gray-900">ölçülmüş veridir</span>:
                  {vol.basis && (
                    <> son <span className="font-semibold text-gray-900">{vol.basis.daysCounted} günün</span> her günü
                    için toplam olay sayısı (canlı GA4 akışı, <span className="font-mono text-[13px]">ga_events_raw</span>)
                    hesaplanır; “günlük hacim” bu {vol.basis.daysCounted} günlük değerin ortalamasıdır. Bu dönemde
                    toplam <span className="font-semibold text-gray-900">{fmtCompact(vol.basis.totalEvents)} olay</span> ölçüldü
                    (en düşük gün {fmtCompact(vol.basis.minEventsPerDay)}, en yüksek gün {fmtCompact(vol.peakEventsPerDay)}).</>
                  )}
                </p>

                {daily.length > 0 && (
                  <>
                    <div className="mt-8 text-sm font-semibold text-gray-700">Son {daily.length} günün günlük olay hacmi</div>
                    <DailyBars daily={daily} freeLimit={vol.freeBqDailyLimit} />
                    <p className="mt-5 text-[15px] leading-relaxed text-gray-600 max-w-2xl">
                      Grafikteki <span className="font-semibold text-rose-600">kesikli çizgi</span>, ücretsiz GA4’ün
                      BigQuery’ye günde aktarabildiği {fmtCompact(vol.freeBqDailyLimit)} olaylık tavandır.
                      Görüldüğü gibi <span className="font-semibold text-gray-900">her gün</span> bu tavanın kat kat üzerindeyiz —
                      yani her gün verimizin büyük kısmı ham olarak elimize hiç ulaşmıyor.
                    </p>
                  </>
                )}
              </Slide>
            )}

            {/* ── 03 Bu hacim neleri içeriyor (kırılım) ──────────────────── */}
            {comp && comp.items?.length > 0 && (
              <Slide n="03" kicker="Bu Hacim Neleri İçeriyor">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {fmtCompact(comp.perDayTotal)} olay/gün · {comp.distinctEventTypes} farklı olay tipi
                </h2>
                <p className="mt-2 text-[15px] text-gray-500 max-w-2xl">
                  Günlük hacmimizi oluşturan olay tipleri (son {comp.daysCounted} gün ortalaması). Her satın alma
                  yolculuğu onlarca ölçüm olayı üretir — listeleme, arama, görüntüleme, etkileşim, sepet ve ödeme adımları.
                </p>

                <div className="mt-6 space-y-2">
                  {comp.items.map((it) => (
                    <div key={it.eventName} className="flex items-center gap-3">
                      <div className="w-52 shrink-0 text-sm">
                        <div className="font-semibold text-gray-800 leading-tight">{evLabel(it.eventName)}</div>
                        <div className="text-[10px] font-mono text-gray-400">{it.eventName}</div>
                      </div>
                      <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                        <div className="h-full bg-indigo-500/85 rounded"
                          style={{ width: `${Math.max(it.pct * 100, 1.5)}%` }} />
                      </div>
                      <div className="w-28 shrink-0 text-right text-sm tabular-nums">
                        <span className="font-bold text-gray-900">{fmtCompact(it.perDay)}</span>
                        <span className="text-gray-400 text-xs">/gün</span>
                      </div>
                      <div className="w-12 shrink-0 text-right text-sm font-semibold text-gray-500 tabular-nums">
                        {fmtPct(it.pct, 0)}
                      </div>
                    </div>
                  ))}
                  {comp.other?.total > 0 && (
                    <div className="flex items-center gap-3 pt-1">
                      <div className="w-52 shrink-0 text-sm text-gray-500 italic">
                        Diğer {comp.other.eventTypeCount} olay tipi
                      </div>
                      <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                        <div className="h-full bg-gray-300 rounded" style={{ width: `${Math.max(comp.other.pct * 100, 1.5)}%` }} />
                      </div>
                      <div className="w-28 shrink-0 text-right text-sm tabular-nums">
                        <span className="font-bold text-gray-700">{fmtCompact(comp.other.perDay)}</span>
                        <span className="text-gray-400 text-xs">/gün</span>
                      </div>
                      <div className="w-12 shrink-0 text-right text-sm font-semibold text-gray-500 tabular-nums">
                        {fmtPct(comp.other.pct, 0)}
                      </div>
                    </div>
                  )}
                </div>

                <p className="mt-6 text-[15px] leading-relaxed text-gray-600 max-w-2xl">
                  Bu olayların hepsi analiz için değerlidir — ürün listesi, arama ve etkileşim olayları olmadan
                  müşteri davranışını anlamak mümkün değil. Ancak ücretsiz sürümde bunların büyük bölümü ham veriye
                  hiç düşmediği için, olay bazlı derin analiz yapılamıyor. GA4 360 bu olayların
                  <span className="font-semibold text-gray-900"> tamamını</span> BigQuery’ye aktarır.
                </p>
              </Slide>
            )}

            {/* ── 04 Durum: limitler vs bizim kullanım ───────────────────── */}
            {vol && (
              <Slide n="04" kicker="Limitler vs Bizim Kullanımımız">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Ücretsiz limitlerin çok üstündeyiz</h2>
                <p className="mt-2 text-[15px] text-gray-500 max-w-2xl">
                  Aşağıdaki tüm “Koton” sütunu, markanın canlı GA4 verisinden (son 30 gün ortalaması).
                </p>

                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" style={{ minWidth: '640px' }}>
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                        <th className="text-left font-semibold px-4 py-3">Metrik</th>
                        <th className="text-right font-semibold px-4 py-3">GA4 Ücretsiz</th>
                        <th className="text-right font-semibold px-4 py-3">GA4 360</th>
                        <th className="text-right font-semibold px-4 py-3 text-gray-900">Koton (bizim)</th>
                        <th className="text-right font-semibold px-4 py-3">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="tabular-nums">
                      {[
                        {
                          metrik: 'Günlük olay hacmi (BigQuery ham veri)', birim: 'olay / gün',
                          free: `${fmtCompact(vol.freeBqDailyLimit)}`, ga360: 'Milyarlar (pratikte sınırsız)',
                          biz: `${fmtCompact(vol.avgEventsPerDay)}`, factor: vol.overFactorAvg,
                        },
                        {
                          metrik: 'Zirve gün', birim: 'olay / gün',
                          free: `${fmtCompact(vol.freeBqDailyLimit)}`, ga360: 'Milyarlar (pratikte sınırsız)',
                          biz: `${fmtCompact(vol.peakEventsPerDay)}`, factor: vol.overFactorPeak,
                        },
                        {
                          metrik: 'Aylık toplam olay', birim: 'olay / ay',
                          free: `≈ ${fmtCompact(vol.freeBqDailyLimit * 30)}`, ga360: 'Tamamı aktarılır',
                          biz: `${fmtCompact(vol.avgEventsPerMonth)}`, factor: vol.overFactorAvg,
                        },
                        {
                          metrik: 'Raporlarda örnekleme eşiği', birim: 'olay / sorgu',
                          free: `${fmtCompact(vol.freeExploreSampleThreshold)}`, ga360: `${fmtCompact(vol.ga360ExploreSampleThreshold)}`,
                          biz: `~${fmtCompact(vol.avgEventsPerDay)} / gün`,
                          note: vol.hoursToFreeSampleThreshold != null
                            ? `eşik ~${vol.hoursToFreeSampleThreshold.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} saatte aşılıyor`
                            : 'eşik aşılıyor',
                        },
                      ].map((r) => (
                        <tr key={r.metrik} className="border-t border-gray-200">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-800">{r.metrik}</div>
                            <div className="text-xs text-gray-400">{r.birim}</div>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">{r.free}</td>
                          <td className="px-4 py-3 text-right text-gray-600">{r.ga360}</td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">{r.biz}</td>
                          <td className="px-4 py-3 text-right">
                            {r.factor != null ? (
                              <span className="inline-block px-2 py-1 rounded-md bg-rose-100 text-rose-700 font-bold text-xs whitespace-nowrap">
                                {r.factor.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}× üstünde
                              </span>
                            ) : (
                              <span className="text-xs text-rose-600 font-medium">{r.note}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-6 text-[15px] leading-relaxed text-gray-600 max-w-2xl">
                  Özetle: günde ortalama <span className="font-semibold text-gray-900">{fmtCompact(vol.avgEventsPerDay)} olay</span> üretiyoruz;
                  bu, ücretsiz sürümün ham veri aktarım tavanının
                  <span className="font-semibold text-gray-900"> {vol.overFactorAvg.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} katı</span>.
                  Sonuç olarak verimizin yaklaşık <span className="font-semibold text-gray-900">{fmtPct(vol.freeBqDroppedPct, 0)}’i</span> hiç
                  dışa aktarılmıyor ve raporlarımız örneklemeye (tahmine) düşüyor.
                </p>
              </Slide>
            )}

            {/* ── 05 Bedeli: kör noktalar ────────────────────────────────── */}
            {vol && (
              <Slide n="05" kicker="Bunun Bedeli">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Kararlarımız tahmine dayanıyor</h2>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border-l-2 border-gray-900 pl-5">
                    <div className="text-sm font-bold text-gray-900">Raporlar örnekleniyor</div>
                    <p className="mt-2 text-[15px] leading-relaxed text-gray-600">
                      Ücretsiz sürümde 10 milyon olayı aşan her analiz örnekleme (tahmin) ile döner.
                      Bizim hacmimizde bu eşik günde yaklaşık <span className="font-semibold text-gray-900">
                      {vol.hoursToFreeSampleThreshold != null ? `${vol.hoursToFreeSampleThreshold.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} saatte` : 'kısa sürede'}</span> aşılıyor
                      — pratikte tüm derin funnel ve segment analizleri tahmini sonuç veriyor.
                    </p>
                  </div>
                  <div className="border-l-2 border-gray-900 pl-5">
                    <div className="text-sm font-bold text-gray-900">Ham veriye erişemiyoruz</div>
                    <p className="mt-2 text-[15px] leading-relaxed text-gray-600">
                      BigQuery’ye günde yalnızca {fmtCompact(vol.freeBqDailyLimit)} olay düştüğü için,
                      olay bazlı gerçek attribution modelleri, ürün-bazlı kârlılık ve makine öğrenmesi
                      çalışmaları kurulamıyor. Elimizde verinin yalnızca bir dilimi var.
                    </p>
                  </div>
                </div>
                {vol && vol.peakDroppedEvents > 0 && (
                  <div className="mt-6 rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">Somut kanıt:</span> En yoğun günümüzde
                    {vol.peakEventsDate ? ` (${trDate(vol.peakEventsDate)})` : ''} <span className="font-semibold text-gray-900">{fmtCompact(vol.peakEventsPerDay)} olay</span> ölçüldü.
                    Ücretsiz sürümün günlük BigQuery export tavanı {fmtCompact(vol.freeBqDailyLimit)} olay — yani o günkü verinin
                    yalnızca <span className="font-semibold text-gray-900">{fmtPct(1 - vol.peakDroppedPct, 0)}’i</span> aktarılabilirdi.
                    Kalan <span className="font-semibold text-gray-900">~{fmtCompact(vol.peakDroppedEvents)} olay</span>, olay kurulumundan
                    tamamen bağımsız olarak yalnızca hacim tavanı yüzünden analiz için kalıcı biçimde kayıptır.
                  </div>
                )}
              </Slide>
            )}

            {/* ── 06 Darboğaz analizi: funnel ────────────────────────────── */}
            {funnel.length > 0 && (
              <Slide n="06" kicker="Darboğaz Analizi">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Satın alma hunisi ve darboğaz</h2>
                <p className="mt-2 text-[15px] text-gray-500 max-w-2xl">
                  Aşağıdaki huni Koton’un canlı GA4 verisinden. En düşük geçiş oranına sahip adım,
                  iyileştirmeye en açık nokta.
                </p>

                <div className="mt-7 space-y-2.5">
                  {funnel.map((s, i) => {
                    const widthPct = Math.max(s.fromTop * 100, 3);
                    const isCritical = worst && s.label === worst.toLabel;
                    return (
                      <div key={s.key} className={`rounded-lg ${isCritical ? 'bg-rose-50 -mx-2 px-2 py-1.5' : ''}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-semibold text-gray-800">{i + 1}. {s.label}</span>
                          <span className="text-gray-500 tabular-nums">
                            {fmtInt(s.count)}
                            {i > 0 && <span className={`ml-3 font-semibold ${isCritical ? 'text-rose-600' : 'text-gray-600'}`}>bir önceki adımdan {fmtPct(s.fromPrev)}</span>}
                          </span>
                        </div>
                        <div className="h-7 bg-gray-100 rounded overflow-hidden">
                          <div className={`h-full rounded ${isCritical ? 'bg-rose-500' : 'bg-gray-800'}`} style={{ width: `${widthPct}%` }} />
                        </div>
                        {isCritical && (
                          <div className="mt-1 text-xs font-semibold text-rose-600 uppercase tracking-wide">Kritik geçiş</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {worst && (
                  <div className="mt-7 border-t border-gray-200 pt-6">
                    <p className="text-[15px] leading-relaxed text-gray-700 max-w-2xl">
                      En düşük geçiş <span className="font-semibold text-gray-900">“{worst.fromLabel} → {worst.toLabel}”</span> adımında:
                      kullanıcıların yalnızca {fmtPct(worst.continuation)}’i ilerliyor; seçili dönemde bu adımda
                      yaklaşık <span className="font-semibold text-gray-900">{fmtCompact(worst.lost)}</span> kullanıcı düşüyor.
                      GA4 360 ile bu adım cihaz, kanal ve kampanya kırılımında ayrıştırılabilir; hangi segmentte
                      kaybın yoğunlaştığı net görülür.
                    </p>
                  </div>
                )}

              </Slide>
            )}

            {/* ── 07 GA4 360 ne değiştiriyor ─────────────────────────────── */}
            <Slide n="07" kicker="GA4 360 Ne Değiştiriyor">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Kısa vadede somut kazanımlar</h2>
              <div className="mt-8 divide-y divide-gray-200">
                {[
                  { t: 'Kayıpsız ham veri', d: 'Günlük aktarım tavanı kalkar; tüm olay akışı BigQuery’ye düşer. Gerçek attribution, LTV ve ürün kârlılığı ilk günden kurulabilir.', koton: vol ? `Bugün kaybolan ~${fmtCompact(vol.freeBqDroppedPerDay)} olay/gün geri gelir.` : null },
                  { t: 'Örnekleme biter', d: 'Analizler tahmine düşmez; funnel ve segment kırılımları tam sayılarla gelir. Yönetim kararları güvenilir veriye oturur.', koton: 'Her raporun tahmini olduğu mevcut durum ortadan kalkar.' },
                  { t: 'Darboğaza hedefli müdahale', d: 'Kritik geçişi cihaz, kanal ve kampanya bazında ayrıştırıp en zayıf segmentten başlayarak iyileştiririz.', koton: worst ? `İlk hedef: ${worst.fromLabel} → ${worst.toLabel}.` : null },
                  { t: 'Marka/kanal görünürlüğü', d: 'Alt-mülkler ve roll-up mülklerle web + uygulama + kampanya verisi hem birleşik hem ayrı raporlanır; ekipler kendi kırılımına anında erişir.', koton: null },
                  { t: 'Uzun dönem & SLA', d: '50 aya kadar veri saklama ile yıl-yıl kıyas; toplama, raporlama ve export için %99,9 hizmet garantisi.', koton: null },
                ].map((r) => (
                  <div key={r.t} className="py-4 flex flex-col md:flex-row md:items-baseline gap-1 md:gap-6">
                    <div className="md:w-56 shrink-0 font-bold text-gray-900">{r.t}</div>
                    <div className="text-[15px] text-gray-600 leading-relaxed">
                      {r.d}
                      {r.koton && <span className="block mt-1 text-sm font-medium text-gray-900">Koton: {r.koton}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Slide>

            <div className="gt-noprint text-center text-xs text-gray-400 pt-2 pb-8">
              Tüm sayılar Koton’un canlı GA4 verisinden üretilmiştir · {data?.generatedAt ? new Date(data.generatedAt).toLocaleString('tr-TR') : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
